import {useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {kreirajThermoBoardPakete} from '../../../../api/thermoboardNalozi'
import notifyToast from '../../../../components/Toast/Toast'

type ThermoBoardKreirajPaketeModalProps = {
  isOpen: boolean
  onClose: () => void
  vrsta?: string | number
  idNaloga?: number | string
  onSuccess?: () => void
  onCreatePakingListe?: (firstId: number | string, lastId: number | string) => void
}

type KreiraniPaketiInfo = {
  firstId: number | string
  lastId: number | string
  brojKreiranihPaketa: number | string
}

const ThermoBoardKreirajPaketeModal: React.FC<ThermoBoardKreirajPaketeModalProps> = ({
  isOpen,
  onClose,
  vrsta,
  idNaloga,
  onSuccess,
  onCreatePakingListe,
}) => {
  const [mega, setMega] = useState('')
  const [obicno, setObicno] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [kreiraniPaketi, setKreiraniPaketi] = useState<KreiraniPaketiInfo | null>(null)

  const description = useMemo(() => {
    const items: string[] = []

    if (Number(mega) > 0) {
      items.push(`Kreirati ${mega} paketa za Megu.`)
    }

    if (Number(obicno) > 0) {
      items.push(`\n Kreirati  ${obicno} paketa za Obicno pakovanje.`)
    }

    return items.length ? items.join(' ') : 'Unesite broj paketa za Mega ili Obicno pakovanje.'
  }, [mega, obicno])

  const handleClose = () => {
    setMega('')
    setObicno('')
    setKreiraniPaketi(null)
    onClose()
  }

  const handleSubmit = async () => {
    setSubmitting(true)

    try {
      const response = await kreirajThermoBoardPakete({
        mega,
        obicno,
        tipPanela: vrsta,
        idNaloga,
      })

      if (response?.success || response?.error === 0) {
        notifyToast(true, response?.message || 'Paketi su uspjesno kreirani.')
        setKreiraniPaketi(response?.data || null)
        onSuccess?.()
        return
      }

      notifyToast(false, response?.message || 'Kreiranje paketa nije uspjelo.')
    } catch (error: any) {
      notifyToast(
        false,
        error?.response?.data?.message || error?.message || 'Greska pri kreiranju paketa.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal show={isOpen} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title className='fw-bold'>Kreiraj Pakete za {vrsta || '-'}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {kreiraniPaketi ? (
          <div className='rounded border border-success bg-light-success p-6'>
            <div className='d-flex align-items-center mb-5'>
              <div className='symbol symbol-45px me-4'>
                <div className='symbol-label bg-success text-white fw-bold fs-3'>OK</div>
              </div>
              <div>
                <div className='fw-bold fs-4 text-gray-900'>Paketi su uspjesno kreirani</div>
                <div className='text-muted fw-semibold'>
                  Pregled kreiranih paketa za {vrsta || '-'}
                </div>
              </div>
            </div>

            <div className='row g-4'>
              <div className='col-md-4'>
                <div className='bg-white rounded p-4 h-100'>
                  <div className='text-muted fw-semibold mb-1'>Kreirano paketa</div>
                  <div className='fw-bold fs-2 text-gray-900'>
                    {kreiraniPaketi.brojKreiranihPaketa}
                  </div>
                </div>
              </div>

              <div className='col-md-4'>
                <div className='bg-white rounded p-4 h-100'>
                  <div className='text-muted fw-semibold mb-1'>Prvi paket ID</div>
                  <div className='fw-bold fs-2 text-gray-900'>{kreiraniPaketi.firstId}</div>
                </div>
              </div>

              <div className='col-md-4'>
                <div className='bg-white rounded p-4 h-100'>
                  <div className='text-muted fw-semibold mb-1'>Zadnji paket ID</div>
                  <div className='fw-bold fs-2 text-gray-900'>{kreiraniPaketi.lastId}</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className='row g-5'>
              <div className='col-md-6'>
                <label className='form-label fw-bold fs-6'>Mega</label>
                <input
                  type='number'
                  min='0'
                  step='1'
                  value={mega}
                  onChange={(event) => setMega(event.target.value)}
                  className='form-control form-control-lg form-control-solid'
                  placeholder='0'
                />
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-bold fs-6'>Obicno pakovanje</label>
                <input
                  type='number'
                  min='0'
                  step='1'
                  value={obicno}
                  onChange={(event) => setObicno(event.target.value)}
                  className='form-control form-control-lg form-control-solid'
                  placeholder='0'
                />
              </div>
            </div>

            <div className='rounded bg-light-primary p-4 mt-6 text-primary fw-semibold'>
              {description}
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button type='button' className='btn btn-light' onClick={handleClose} disabled={submitting}>
          Zatvori
        </button>
        {kreiraniPaketi ? (
          <button
            type='button'
            className='btn btn-success fw-bold'
            onClick={() => onCreatePakingListe?.(kreiraniPaketi.firstId, kreiraniPaketi.lastId)}
          >
            Kreiraj paking liste za ove pakete
          </button>
        ) : (
          <button
            type='button'
            className='btn btn-primary fw-bold'
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className='spinner-border spinner-border-sm me-2' />
                Kreiranje...
              </>
            ) : (
              'Kreiraj Pakete'
            )}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default ThermoBoardKreirajPaketeModal
